import TaskService, { QueryTaskRequest } from '@/core/Services/TaskService';
import { debounce } from 'debounce';
import { Ref, shallowRef, watch } from 'vue';
import UserService, { QueryUserRequest } from '@/core/Services/UserService';
import NumericCounterService from '@/core/Services/NumericCounterService';
import IQueryService from '@/core/Services/IQueryService';
import ITask from '@/core/Models/ITask';
import IUser from '@/core/Models/IUser';
import IQueryNumericCounterRequest from '@/core/Requests/IQueryNumericCounterRequest';
import INumericCounter from '@/core/Models/INumericCounter';
import IQueryNumericCounterOptionsRequest from '@/core/Requests/IQueryNumericCounterUnitsRequest';
import NumericCounterUnitService from '@/core/Services/NumericCounterUnitService';
import NumericCounterTagNameService from '@/core/Services/NumericCounterTagNameService';
import NumericCounterTagValueService from '@/core/Services/NumericCounterTagValueService';
import { useI18n } from 'vue-i18n';
import NumericCompletionCondition from '@/core/Values/NumericCompletionCondition';
import NumericViewMode from '@/core/Values/NumericViewMode';

interface ISelectedOption<TValue> {
    value: TValue;
    label: string;
}

type DebounceSearchFunction = ((search: string, loading: (isLoading: boolean) => void) => Promise<void>) & {
    clear(): void;
} & {
    flush(): void;
};

export const useTaskSelector = defaultSelector<QueryTaskRequest, ITask, ITask, number>(
    TaskService,
    null,
    (value) => value.id,
    'title',
);

export const useUserSelector = defaultSelector<QueryUserRequest, IUser, string, string>(
    UserService,
    (user) => user.id,
    (userId) => userId,
);

export const useNumericCounterUnitSelector = staticSelector<IQueryNumericCounterOptionsRequest, string, string, string>(
    NumericCounterUnitService,
);
export const useNumericCounterTagNameSelector = (
    additionalQuery: Ref<IQueryNumericCounterOptionsRequest> | null = null,
    defaultValues: string[] = [],
) => {
    const { t, te } = useI18n();

    return staticSelector<IQueryNumericCounterOptionsRequest, string, ISelectedOption<string>, string>(
        NumericCounterTagNameService,
        (value) => ({
            value,
            label: value.startsWith('kpi-numerics.system') ? (te(value) ? t(value) : value) : value,
        }),
        (value) => value.value,
    )(additionalQuery, defaultValues);
};
export const useNumericCounterTagValueSelector = defaultSelector<
    IQueryNumericCounterOptionsRequest,
    string,
    string,
    string
>(NumericCounterTagValueService, null, (numericCounterTagValue) => numericCounterTagValue);

export const useNumericCounterSelector = defaultSelector<
    IQueryNumericCounterRequest,
    INumericCounter,
    INumericCounter,
    number
>(NumericCounterService, null, (numericCounter) => numericCounter.id, 'name');

export const useNumericConditionSelector = () => {
    const { t } = useI18n();

    return hardcodeSelector<
        NumericCompletionCondition,
        ISelectedOption<NumericCompletionCondition>,
        NumericCompletionCondition
    >(
        Object.values(NumericCompletionCondition).filter(
            (x) => !isNaN(parseInt(x as string)),
        ) as NumericCompletionCondition[],
        (condition) => ({ value: condition, label: t('numeric.condition-' + condition) }),
        (condition) => condition.value,
        'label',
    )();
};

export const useNumericViewModeSelector = () => {
    const { t } = useI18n();

    return hardcodeSelector<NumericViewMode, ISelectedOption<NumericViewMode>, NumericViewMode>(
        Object.values(NumericViewMode).filter((x) => !isNaN(parseInt(x as string))) as NumericViewMode[],
        (viewMode) => ({ value: viewMode, label: t('numeric.view-mode-' + viewMode) }),
        (viewMode) => viewMode.value,
        'label',
    )();
};

function defaultSelector<TQuery, TModel, TSelectOption, TReducedValue>(
    queryService: IQueryService<TQuery, TModel>,
    map: ((model: TModel) => TSelectOption) | null,
    reduce: (value: TSelectOption) => TReducedValue,
    label = 'label',
) {
    let initialOptions: TModel[] | TSelectOption[] | null = null;

    return (additionalQuery: Ref<TQuery> | null = null, defaultValues: TModel[] = []) => {
        const options = shallowRef<TModel[] | TSelectOption[] | null>(initialOptions ?? []);

        if (additionalQuery) {
            watch(
                () => additionalQuery.value,
                () => fetchAsync('', true),
            );
        }

        function prefetch() {
            fetchAsync();
        }

        async function fetchAsync(search = '', forceFetch = false): Promise<void> {
            if (search === '' && initialOptions && !forceFetch) {
                options.value = initialOptions;
                return;
            }

            let result = await queryService.queryAsync(
                Object.assign(
                    {
                        page: 1,
                        perPage: 10,
                        search,
                    },
                    additionalQuery?.value,
                ),
            );

            result = [...defaultValues, ...result];
            options.value = map ? result.map(map) : result;

            if (!initialOptions || forceFetch) {
                initialOptions = options.value;
            }
        }

        let debounceSearchFunction = null as DebounceSearchFunction | null;
        async function searchAsync(search: string, loading: (isLoading: boolean) => void): Promise<void> {
            if (!debounceSearchFunction) {
                debounceSearchFunction = debounce(async (search: string, loading: (isLoading: boolean) => void) => {
                    loading(true);
                    try {
                        await fetchAsync(search);
                    } finally {
                        loading(false);
                        debounceSearchFunction = null;
                    }
                }, 500);
            }

            debounceSearchFunction(search, loading);
        }

        return { label, options, map, reduce, prefetch, fetchAsync, searchAsync };
    };
}

function staticSelector<TQuery, TModel, TSelectOption, TReducedValue>(
    queryService: IQueryService<TQuery, TModel>,
    map: ((model: TModel) => TSelectOption) | null = null,
    reduce: ((value: TSelectOption) => TReducedValue) | null = null,
    label = 'label',
) {
    let fetched = false;
    const options = shallowRef<TModel[] | TSelectOption[] | null>([]);

    return (additionalQuery: Ref<TQuery> | null = null, defaultValues: TModel[] = []) => {
        if (additionalQuery) {
            watch(
                () => additionalQuery.value,
                () => fetchAsync,
            );
        }

        async function prefetch() {
            if (fetched) {
                return;
            }
            fetchAsync();
        }

        async function fetchAsync() {
            if (fetched) {
                return;
            }

            let result = await queryService.queryAsync(
                Object.assign(
                    {
                        page: 1,
                        perPage: 10,
                    },
                    additionalQuery?.value,
                ),
            );

            result = [...defaultValues, ...result.filter((item) => !defaultValues.includes(item))];
            options.value = map ? result.map(map) : result;
            fetched = true;
        }

        return { label, options, map, reduce, prefetch };
    };
}

function hardcodeSelector<TModel, TSelectOption, TReducedValue>(
    defaultValues: TModel[],
    map: ((model: TModel) => TSelectOption) | null = null,
    reduce: ((value: TSelectOption) => TReducedValue) | null = null,
    label = 'label',
) {
    const options = map ? defaultValues.map(map) : defaultValues;

    return () => {
        return { label, options, map, reduce };
    };
}
